/*
 * Module for the quick create example editor
 */

define([
  "marionette",
  "messaging/messenger",
  "modules/quickCreate/quickExampleModal/quickExampleModalView",
  "modules/quickCreate/quickExampleModal/quickExamplesList/quickExamplesListModule",
  "modules/topicsSplit/topic/topicExampleModal/exampleEditor/exampleEditorModule",
  "modules/topicsSplit/topic/topicExampleModal/exampleTranslation/exampleTranslationModule",
  "modules/topicsSplit/topic/topicExampleModal/strengthGauge/strengthGaugeModule",
  "modules/topicsSplit/topic/topicExampleModal/exampleLocale/exampleLocaleModule",
], function (
  Marionette,
  Messenger,
  QuickExampleModalView,
  QuickExamplesListModule,
  ExampleEditorModule,
  ExampleTranslationModule,
  StrengthGaugeModule,
  ExampleLocaleModule,
) {
  return Marionette.Controller.extend({
    initialize: function (options) {
      this.name = "quickExampleModal";
      this.region = options.region;
      this.selectedLocale = Messenger.reqres.request("default:response:language");
      this._setUpReqres();
      this._setUpVents();
    },
    show: function (topic) {
      this.failureThreshold = Messenger.reqres.request("example:failure:threshold");
      const quickCreate = topic.quickCreate();
      const examples = topic.examples();
      this.topic = topic;
      this.layout = new QuickExampleModalView.quickExampleModalLayout({ model: quickCreate });

      //load sub modules
      this.list = this.addSubModule(new QuickExamplesListModule({ region: this.layout.list }));
      this.editor = this.addSubModule(new ExampleEditorModule({ region: this.layout.editor }));
      this.gauge = this.addSubModule(new StrengthGaugeModule({ region: this.layout.gauge }));
      this.translation = this.addSubModule(
        new ExampleTranslationModule({ region: this.layout.translation }),
      );
      this.locale = this.addSubModule(
        new ExampleLocaleModule({
          region: this.layout.locale,
          selectedLocale: this.selectedLocale,
          showOnlyEnglishLocales: false,
        }),
      );

      this.layout.on(
        "done",
        function () {
          //update the quick create model based on the 'marked as completed' checkbox
          this._updateQuickCreate(quickCreate);
          //delete any examples that were deleted by the user
          this.list.finalizeDeletions();
          //check to make sure the strength of the current example is up to code
          this.list.validateExampleBefore(null, function () {
            //save the examples (by saving the children of the topic) then close the modal
            topic.saveChildren({
              success: function () {
                Messenger.vent.trigger("close:modal");
              },
              error: function (model, response) {
                Messenger.showErrorFromResponse(response);
              },
            });
          });
        },
        this,
      );

      this.layout.on(
        "cancel",
        function () {
          //we want to revert all the changes we made.
          //To do this, we just reset and fetch a clean copy of the examples collection
          topic.examples().reset();
          if (!topic.isNew()) {
            topic.examples().fetch();
          }
          Messenger.vent.trigger("close:modal");
        },
        this,
      );

      this.layout.on(
        "show",
        function () {
          this.list.show(examples);
        },
        this,
      );

      this.showRegion(this.layout);
    },
    _updateQuickCreate: function (quickCreate) {
      //if the completed checkbox is enabled, the set component as completed
      if (this.layout.getCompleteness()) {
        quickCreate.set("exampleComponent", 2);
      } else {
        //we have at minimum created a single example, so set it as "started"
        quickCreate.set("exampleComponent", 1);
      } //otherwise, leave it alone.
    },
    _setUpReqres: function () {
      Messenger.reqres.setHandler("current:topic", () => this.topic, this);
      Messenger.reqres.setHandler("current:topic:id", () => this.topic.id, this);
      Messenger.reqres.setHandler("current:topic:status", () => this.topic.get("statusId"), this);
    },
    _setUpVents: function () {
      //when an example is clicked, show the editor and strength gauge
      Messenger.vent.on(
        "example:selected",
        function (example) {
          //set the example's localeId in the dropdown. Otherwise, the default will always be selected when loaded
          Messenger.vent.trigger("set:topic:example:locale", example.get("localeId"));
          this.locale.show(example);
          this.gauge.show(example);
          this.editor.show(example);
          this.translation.show(example);
        },
        this,
      );

      //the strength gauge was updated, so make sure that we have a passing example.
      //Otherwise, we retrict the ability to change examples, create new examples, or hit the done button
      Messenger.vent.on(
        "strength:updated",
        function (strengthScore) {
          if (strengthScore <= this.failureThreshold) {
            this.layout.disableDoneButton();
          } else {
            this.layout.enableDoneButton();
          }
        },
        this,
      );

      Messenger.vent.on(
        "hide:example:buttons",
        function (isHidden) {
          // Notify the editor sub-module to hide the optional and substitution buttons
          this.editor.hide(isHidden);
        },
        this,
      );
      Messenger.vent.on(
        "hide:translate:button",
        function (isHidden) {
          // Notify the editor sub-module to hide the translate button
          this.editor.hideTranslateButton(isHidden);
        },
        this,
      );
    },
  });
});
